/**
 * PayrollPipeline — แถบ pipeline ปิดงวดเงินเดือนที่ใช้ร่วมทุกหน้าในสาย payroll
 * (สรุปเวลา → ตรวจก่อนปิดงวด → ทำเงินเดือน → ส่งสลิป) เพื่อให้ผู้ใช้รู้เสมอว่า
 * ตอนนี้อยู่ขั้นไหนของรอบ และกดข้ามไปขั้นอื่นได้. เป็น nav ล้วน — ไม่ดึงข้อมูลเอง
 * แต่ละหน้าส่ง `current` ของตัวเองเข้ามา.
 */
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

export type PipelineStage = 'reconcile' | 'close' | 'payroll' | 'slips';

// Routes point at the consolidated /owner/cycle hub (tabs preserve deep links via hash).
const STAGES: Array<{ id: PipelineStage; label: string; route: string }> = [
  { id: 'reconcile', label: 'สรุปเวลา', route: '/owner/cycle#reconcile' },
  { id: 'close', label: 'ตรวจก่อนปิดงวด', route: '/owner/cycle#close' },
  { id: 'payroll', label: 'ทำเงินเดือน', route: '/owner/cycle#payroll' },
  { id: 'slips', label: 'ส่งสลิป', route: '/owner/cycle#payroll' },
];

export function PayrollPipeline({ current, period }: { current: PipelineStage; period?: string }) {
  const currentIdx = STAGES.findIndex((s) => s.id === current);

  return (
    <div className="card p-3 mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500">ขั้นตอนปิดงวด</span>
        {period && <span className="text-xs font-medium text-gray-600">งวด {period}</span>}
      </div>
      <div className="flex items-center gap-1 overflow-x-auto">
        {STAGES.map((s, i) => {
          const done = i < currentIdx;
          const active = i === currentIdx;
          return (
            <div key={s.id} className="flex items-center gap-1 shrink-0">
              <Link to={s.route} className="flex items-center gap-1.5 group">
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold transition ${
                    active
                      ? 'bg-brand-600 text-white ring-2 ring-brand-200'
                      : done
                        ? 'bg-brand-600 text-white'
                        : 'bg-gray-200 text-gray-500 group-hover:bg-gray-300'
                  }`}
                >
                  {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </span>
                <span
                  className={`text-xs whitespace-nowrap ${
                    active ? 'text-gray-900 font-semibold' : done ? 'text-gray-700' : 'text-gray-400'
                  }`}
                >
                  {s.label}
                </span>
              </Link>
              {i < STAGES.length - 1 && <div className={`w-4 h-0.5 ${done ? 'bg-brand-400' : 'bg-gray-200'}`} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
